import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';

import {
  threadHistoryState,
  useAuth,
  useChatData,
  useChatInteract,
  useChatMessages,
  useConfig
} from '@chainlit/react-client';

import Alert from '@/components/Alert';
import HaloBackground from '@/components/HaloBackground';
import { hasMessage } from '@/lib/utils';
import { TaskList } from '@/components/Tasklist';
import { Translator } from 'components/i18n';
import { useTranslation } from 'components/i18n/Translator';

import { useUpload } from '@/hooks/useUpload';
import { useLayoutMaxWidth } from 'hooks/useLayoutMaxWidth';

import {
  AttachmentSource,
  IAttachment,
  attachmentsState
} from 'state/chat';

import { ErrorBoundary } from '../ErrorBoundary';
import ChatFooter from './Footer';
import ChatHeader from './ChatHeader';
import MessagesContainer from './MessagesContainer';
import ScrollContainer from './ScrollContainer';

const Chat = () => {
  const { user } = useAuth();
  const { config } = useConfig();
  const setAttachments = useSetRecoilState(attachmentsState);
  const setThreads = useSetRecoilState(threadHistoryState);

  const autoScrollRef = useRef(true);
  const { error, disabled, callFn } = useChatData();
  const { uploadFile } = useChatInteract();
  const uploadFileRef = useRef(uploadFile);
  const navigate = useNavigate();

  const fileSpec = useMemo(
    () => ({
      max_size_mb:
        config?.features?.spontaneous_file_upload?.max_size_mb || 20,
      max_files: config?.features?.spontaneous_file_upload?.max_files || 5,
      accept: config?.features?.spontaneous_file_upload?.accept || {
        'image/*': [],
        'application/pdf': ['.pdf'],
        'application/msword': ['.doc'],
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
          '.docx'
        ]
      }
    }),
    [config]
  );

  const { t } = useTranslation();
  const layoutMaxWidth = useLayoutMaxWidth();

  useEffect(() => {
    if (callFn) {
      const event = new CustomEvent('chainlit-call-fn', {
        detail: callFn
      });
      window.dispatchEvent(event);
    }
  }, [callFn]);

  useEffect(() => {
    uploadFileRef.current = uploadFile;
  }, [uploadFile]);

  const onFileUpload = useCallback(
    (payloads: File[], meta?: { source?: AttachmentSource }) => {
      const attachements: IAttachment[] = payloads.map((file) => {
        const id = uuidv4();
        const previewUrl = file.type.startsWith('image/')
          ? URL.createObjectURL(file)
          : undefined;

        const { xhr, promise } = uploadFileRef.current(file, (progress) => {
          setAttachments((prev) =>
            prev.map((attachment) => {
              if (attachment.id === id) {
                return {
                  ...attachment,
                  uploadProgress: progress
                };
              }
              return attachment;
            })
          );
        });

        promise
          .then((res) => {
            setAttachments((prev) =>
              prev.map((attachment) => {
                if (attachment.id === id) {
                  return {
                    ...attachment,
                    serverId: res.id,
                    uploaded: true,
                    uploadProgress: 100,
                    cancel: undefined
                  };
                }
                return attachment;
              })
            );
          })
          .catch((error) => {
            toast.error(
              `${t('chat.fileUpload.errors.failed')} ${file.name}: ${typeof error === 'object' && error !== null
                ? error.message ?? error
                : error
              }`
            );
            if (previewUrl) URL.revokeObjectURL(previewUrl);
            setAttachments((prev) =>
              prev.filter((attachment) => attachment.id !== id)
            );
          });

        const forget = () => {
          if (previewUrl) URL.revokeObjectURL(previewUrl);
          setAttachments((prev) =>
            prev.filter((attachment) => attachment.id !== id)
          );
        };

        return {
          id,
          type: file.type,
          name: file.name,
          size: file.size,
          previewUrl,
          source: meta?.source,
          uploadProgress: 0,
          cancel: () => {
            toast.info(`${t('chat.fileUpload.errors.cancelled')} ${file.name}`);
            xhr.abort();
            forget();
          },
          remove: forget
        };
      });
      setAttachments((prev) => prev.concat(attachements));
    },
    [uploadFile]
  );

  const onFileUploadError = useCallback(
    (error: string) => toast.error(error),
    [toast]
  );

  const upload = useUpload({
    spec: fileSpec,
    onResolved: onFileUpload,
    onError: onFileUploadError,
    options: { noClick: true }
  });

  const { messages, threadId } = useChatMessages();

  useEffect(() => {
    const currentPage = new URL(window.location.href);
    if (
      user &&
      config?.dataPersistence &&
      threadId &&
      currentPage.pathname === '/'
    ) {
      navigate(`/thread/${threadId}`);
    } else {
      setThreads((prev) => ({
        ...prev,
        currentThreadId: threadId
      }));
    }
  }, []);

  const enableAttachments =
    !disabled && config?.features?.spontaneous_file_upload?.enabled;

  const hasMessages = hasMessage(messages);

  return (
    <div
      {...(enableAttachments
        ? upload.getRootProps({ className: 'dropzone' })
        : {})}
      onBlur={undefined}
      onFocus={undefined}
      className="fongpt-chat relative z-10 flex h-full w-full min-h-0 flex-col overflow-hidden"
    >
      <HaloBackground />
      <ChatHeader />
      {enableAttachments ? (
        <input id="#upload-drop-input" {...upload.getInputProps()} />
      ) : null}

      {error ? (
        <div className="relative z-10 w-full mx-auto my-2 pt-14 md:pt-16">
          <Alert className="mx-2" id="session-error" variant="error">
            <Translator path="common.status.error.serverConnection" />
          </Alert>
        </div>
      ) : null}
      <ErrorBoundary>
        {hasMessages ? (
          <ScrollContainer
            autoScrollUserMessage={config?.features?.user_message_autoscroll}
            autoScrollRef={autoScrollRef}
            className="px-4"
          >
            {/* The horizontal padding lives on the scroll container so this
                column measures exactly like the composer wrapper below and the
                two edges line up. */}
            <div
              className="fongpt-message-column relative z-10 flex flex-col mx-auto w-full flex-grow pt-16 md:pt-16"
              style={{ maxWidth: layoutMaxWidth }}
            >
              <TaskList isMobile={true} />
              <MessagesContainer navigate={navigate} />
            </div>
          </ScrollContainer>
        ) : (
          <div className="relative z-10 flex min-h-0 flex-1 items-center justify-center px-4">
            <div className="w-full max-w-[48rem]">
              <ChatFooter
                variant="welcome"
                fileSpec={fileSpec}
                onFileUpload={onFileUpload}
                onFileUploadError={onFileUploadError}
                autoScrollRef={autoScrollRef}
              />
            </div>
          </div>
        )}
        {hasMessages ? (
          <div className="fongpt-chat-footer relative z-20 w-full px-4 pb-4 pt-2 md:pb-6">
            <div className="mx-auto w-full" style={{ maxWidth: layoutMaxWidth }}>
              <ChatFooter
                variant="chat"
                fileSpec={fileSpec}
                onFileUpload={onFileUpload}
                onFileUploadError={onFileUploadError}
                autoScrollRef={autoScrollRef}
              />
            </div>
          </div>
        ) : null}
      </ErrorBoundary>
    </div>
  );
};

export default Chat;
